<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { ContractViewsService } from '@/services/contractManagement/contractViewsService';
import type {
  ContractManagementViewColumnCreate,
  EnumResponseTypeValue,
  ExtractionPromptQuestion,
  ExtractionPromptSearchEntry,
} from '@/api';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import PromptPicker from './PromptPicker.vue';
import QuestionFields from './QuestionFields.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCard from '@/components/design/base/GaiaCard.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaSegmented, {
  type GaiaSegmentedOption,
} from '@/components/design/base/GaiaSegmented.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaDrawer from '@/components/design/base/GaiaDrawer.vue';
import GaiaToggleRow from '@/components/design/composite/GaiaToggleRow.vue';
import type { LinkedPrompt, PreviewColumnData } from '@/types/contractManagement/columnPreview';
import {
  columnQuestion,
  enumOptionsError,
  formToQuestion,
  responseTypeLabel,
} from '@/utils/contractManagementView/columnQuestion';
import { useGaiaToast } from '@/composables/useGaiaToast';

export type { PreviewColumnData };

interface Props {
  show: boolean;
  currentColumnCount: number;
  /** Required for persisted mode - the view UID to add column to */
  viewUid?: string;
  /** If true, emits local data instead of making API calls (for view creation wizard) */
  previewMode?: boolean;
  /** Preview mode: the prompts the wizard's columns already link, marked in the picker. */
  usedPromptUids?: string[];
}

interface Emits {
  (e: 'update:show', value: boolean): void;
  /** Emitted after successful API call (persisted mode) */
  (e: 'created'): void;
  /** Emitted with new column data (preview mode) */
  (e: 'preview-created', column: PreviewColumnData): void;
}

const props = withDefaults(defineProps<Props>(), {
  viewUid: '',
  previewMode: false,
  usedPromptUids: () => [],
});
const emit = defineEmits<Emits>();
const message = useGaiaToast();
const contractTypeStore = useContractTypeStore();

const showDrawer = computed({
  get: () => props.show,
  set: (value: boolean) => {
    emit('update:show', value);
  },
});

const saving = ref(false);

/** Link one of the organisation's prompts, or ask a new question, which becomes a prompt. */
type ColumnMode = 'existing' | 'new';
const mode = ref<ColumnMode>('existing');
const modeOptions: GaiaSegmentedOption[] = [
  { label: 'Add existing', value: 'existing' },
  { label: 'Create new', value: 'new' },
];

// GaiaFormField takes the message as a prop; each is cleared as soon as its field is typed into.
const promptNameError = ref<string>('');
const promptError = ref<string>('');

/** Add existing: the prompt picked from the library, linked as it is. */
const pickedPrompt = ref<LinkedPrompt | null>(null);
/** The picked prompt is already a column of the view; asked unchanged, the save is refused. */
const pickedPromptInView = ref(false);

// Form data. The column name and visibility mean the same in both modes, so they are shared.
interface ColumnForm {
  /** The column's own name; empty: it shows its prompt's display name and follows renames. */
  columnName: string;
  visible: boolean;
  /** Create new: the new prompt's name. */
  promptName: string;
  prompt: string;
  responseType: string;
  exampleData: string;
  enumOptions: EnumResponseTypeValue[];
  /** A new question's contract types, so other views can pick it from them. */
  contractTypeUids: string[];
}

const formData = ref<ColumnForm>(emptyForm());

function emptyForm(): ColumnForm {
  return {
    columnName: '',
    visible: true,
    promptName: '',
    prompt: '',
    responseType: 'str',
    exampleData: '',
    enumOptions: [],
    contractTypeUids: [],
  };
}

const isEnumValid = computed((): boolean => enumOptionsError(formData.value) === null);

/** The name the column shows while its own is left empty. */
const fallbackName = computed((): string =>
  mode.value === 'existing'
    ? (pickedPrompt.value?.display_name ?? '')
    : formData.value.promptName.trim()
);

/** True when the required fields are filled; stamps the messages if not. */
function validateNewQuestion(): boolean {
  promptNameError.value = formData.value.promptName.trim() ? '' : 'Please enter a prompt name';
  promptError.value = formData.value.prompt.trim() ? '' : 'Please enter a prompt or question';
  return !promptNameError.value && !promptError.value;
}

// Reset form when drawer opens
watch(showDrawer, newValue => {
  if (newValue) {
    resetForm();
    void contractTypeStore.ensureLoaded();
  }
});

function resetForm(): void {
  mode.value = 'existing';
  formData.value = emptyForm();
  pickedPrompt.value = null;
  pickedPromptInView.value = false;
  promptNameError.value = '';
  promptError.value = '';
}

const saveButtonText = computed(() =>
  mode.value === 'new' && !props.previewMode ? 'Create column' : 'Add column'
);

// A prompt already in the view would be refused as a second column asking it.
const canSave = computed((): boolean =>
  mode.value === 'existing'
    ? pickedPrompt.value !== null && !pickedPromptInView.value
    : isEnumValid.value
);

/** The column's question and, for the wizard, what it shows; null when the form is not valid. */
function buildColumn(): {
  question: ExtractionPromptQuestion;
  linkedPrompt: LinkedPrompt | null;
} | null {
  if (mode.value === 'existing') {
    const prompt = pickedPrompt.value;
    if (!prompt) return null;
    return {
      question: {
        prompt: prompt.prompt,
        response_type: prompt.response_type,
        example_data: prompt.example_data,
      },
      linkedPrompt: prompt,
    };
  }

  if (!validateNewQuestion()) {
    message.error('Please fill in all required fields');
    return null;
  }
  const optionsError = enumOptionsError(formData.value);
  if (optionsError) {
    message.error(optionsError);
    return null;
  }
  const exampleText = formData.value.exampleData.trim();
  if (exampleText) {
    try {
      JSON.parse(exampleText);
    } catch {
      message.warning('Example data is not valid JSON. It will be saved as a string.');
    }
  }
  return { question: formToQuestion(formData.value), linkedPrompt: null };
}

async function handleSave(): Promise<void> {
  const column = buildColumn();
  if (!column) return;

  const columnName = formData.value.columnName.trim();
  const promptName = formData.value.promptName.trim();
  const contractTypeUids = formData.value.contractTypeUids;

  if (props.previewMode) {
    // Preview mode: emit local data without API call
    emit('preview-created', {
      id: `preview-col-${Date.now()}`,
      name: columnName || fallbackName.value,
      visible: formData.value.visible,
      order_index: props.currentColumnCount,
      question: column.question,
      linkedPrompt: column.linkedPrompt,
      contractTypeUids,
      promptName: column.linkedPrompt ? undefined : promptName,
    });
    message.success('Column added to preview');
    showDrawer.value = false;
    return;
  }

  if (!props.viewUid) {
    message.error('View UID is required');
    return;
  }

  saving.value = true;
  try {
    const columnData: ContractManagementViewColumnCreate = {
      // None: the column shows its prompt's display name.
      name: columnName || null,
      visible: formData.value.visible,
      order_index: props.currentColumnCount,
      filters: undefined,
    };

    await ContractViewsService.addColumn(
      props.viewUid,
      columnData,
      columnQuestion(column.question, column.linkedPrompt, contractTypeUids, promptName)
    );

    message.success(
      column.linkedPrompt
        ? 'Column added'
        : `Column created, along with a new prompt “${promptName}”`
    );
    emit('created');
    showDrawer.value = false;
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Failed to create column';
    message.error(errorMsg);
  } finally {
    saving.value = false;
  }
}

function handleCancel(): void {
  showDrawer.value = false;
}

// A contract type's prompts were added as columns: nothing is left for this drawer to create.
function handleBulkAdded(): void {
  emit('created');
  showDrawer.value = false;
}

function handlePromptSelected(prompt: ExtractionPromptSearchEntry): void {
  pickedPrompt.value = prompt;
  pickedPromptInView.value =
    Boolean(prompt.view_column_uids?.length) || props.usedPromptUids.includes(prompt.uid);
}

function clearPickedPrompt(): void {
  pickedPrompt.value = null;
  pickedPromptInView.value = false;
}
</script>

<template>
  <gaia-drawer
    :show="props.show"
    :width="720"
    title="Add column"
    subtitle="A custom column is a question asked of every contract in the view."
    @update:show="(value: boolean) => (value ? undefined : handleCancel())"
  >
    <n-flex vertical :size="16" :wrap="false">
      <gaia-segmented
        :value="mode"
        :options="modeOptions"
        size="medium"
        block
        @update:value="(value: string) => (mode = value as ColumnMode)"
      />

      <!-- Add existing: pick a prompt first, then name the column. Kept mounted, so it neither
           re-fetches nor loses its place on "Change" or a mode toggle. -->
      <prompt-picker
        v-show="mode === 'existing' && !pickedPrompt"
        :view-uid="previewMode ? undefined : viewUid"
        :used-prompt-uids="usedPromptUids"
        :bulk-add="!previewMode"
        @select-prompt="handlePromptSelected"
        @bulk-added="handleBulkAdded"
      />

      <template v-if="mode === 'existing' && pickedPrompt">
        <gaia-card size="small" content-style="padding: 10px 12px">
          <n-flex vertical :size="6" :wrap="false">
            <n-flex align="center" :size="8" :wrap="false">
              <gaia-text size="body" weight="semibold" tone="ink" truncate>
                {{ pickedPrompt.display_name }}
              </gaia-text>
              <gaia-tag variant="muted" size="tiny">
                {{ responseTypeLabel(pickedPrompt.response_type) }}
              </gaia-tag>
              <gaia-button text size="tiny" style="margin-left: auto" @click="clearPickedPrompt">
                Change
              </gaia-button>
            </n-flex>
            <gaia-text
              size="dense"
              tone="tertiary"
              :style="{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }"
            >
              {{ pickedPrompt.prompt }}
            </gaia-text>
          </n-flex>
        </gaia-card>

        <gaia-banner v-if="pickedPromptInView" type="warning">
          <strong>{{ pickedPrompt.display_name }}</strong> is already a column in this view. Pick
          another prompt.
        </gaia-banner>
      </template>

      <!-- Create new: a new question, which becomes a prompt. -->
      <gaia-form-field
        v-if="mode === 'new'"
        label="Prompt name"
        required
        :error="promptNameError || undefined"
      >
        <gaia-input
          v-model:value="formData.promptName"
          placeholder="e.g. Contract value, Start date"
          :maxlength="200"
          :status="promptNameError ? 'error' : undefined"
          style="width: 100%"
          @update:value="promptNameError = ''"
        />
      </gaia-form-field>

      <template v-if="mode === 'new' || pickedPrompt">
        <gaia-form-field
          label="Column name"
          hint="Leave empty to show the prompt name; the column then follows its renames"
        >
          <gaia-input
            v-model:value="formData.columnName"
            :placeholder="fallbackName || 'Same as the prompt name'"
            :maxlength="200"
            style="width: 100%"
          />
        </gaia-form-field>

        <gaia-toggle-row
          card
          label="Visible"
          hint="Show this column in the view"
          :checked="formData.visible"
          @update:checked="(value: boolean) => (formData.visible = value)"
        />
      </template>

      <template v-if="mode === 'new'">
        <question-fields v-model:prompt-error="promptError" :form="formData" />

        <gaia-form-field
          label="Contract types"
          hint="Other views pick the new prompt from these; leave empty for a standalone prompt"
        >
          <gaia-select
            v-model:value="formData.contractTypeUids"
            multiple
            filterable
            :max-tag-count="3"
            :options="contractTypeStore.uidOptions"
            placeholder="No contract type"
          />
        </gaia-form-field>
      </template>
    </n-flex>

    <template #footer>
      <n-flex justify="end" :size="10">
        <gaia-button @click="handleCancel">Cancel</gaia-button>
        <gaia-button type="primary" :loading="saving" :disabled="!canSave" @click="handleSave">
          {{ saveButtonText }}
        </gaia-button>
      </n-flex>
    </template>
  </gaia-drawer>
</template>
